export const getRealtimeHydrationKey = (shopId, uid) =>
  `${shopId || ""}:${uid || ""}`;

export const isMessageForContactUid = (message, uid) => {
  return message?.messageId != null && uid != null
    ? String(message.messageId) === String(uid)
    : false;
};

export const attachRealtimeShopMetadata = (contact, shop) => {
  if (!contact) return contact;
  if (!shop?._id) return contact;
  return {
    ...contact,
    _shopId: shop._id,
    _shopName: shop.name,
    _shopAvatar: shop.avatar,
  };
};

export const shouldIncludeRealtimeContact = (
  contact,
  selectedCategories = [],
) => {
  const isShowingHidden = selectedCategories.includes("hidden");
  if (isShowingHidden) return Boolean(contact?.isHidden);
  return !contact?.isHidden;
};

const getMessageKeys = (message) => {
  if (!message) return [];
  return [
    message._id != null ? `_id:${message._id}` : null,
    message.msgId != null && message.msgId !== "" ? `msg:${message.msgId}` : null,
    message.cliMsgId != null && message.cliMsgId !== ""
      ? `cli:${message.cliMsgId}`
      : null,
  ]
    .filter(Boolean)
    .map(String);
};

export const mergeRealtimeMessages = (existingMessages, incomingMessages) => {
  if (!Array.isArray(incomingMessages) || incomingMessages.length === 0) {
    return Array.isArray(existingMessages) ? existingMessages : [];
  }
  if (!Array.isArray(existingMessages) || existingMessages.length === 0) {
    return incomingMessages;
  }

  const merged = [...existingMessages];
  const indexByKey = new Map();

  merged.forEach((message, index) => {
    getMessageKeys(message).forEach((key) => indexByKey.set(key, index));
  });

  for (const incomingMessage of incomingMessages) {
    const keys = getMessageKeys(incomingMessage);
    const existingIndex = keys
      .map((key) => indexByKey.get(key))
      .find((index) => index !== undefined);

    if (existingIndex === undefined) {
      const nextIndex = merged.length;
      merged.push(incomingMessage);
      keys.forEach((key) => indexByKey.set(key, nextIndex));
      continue;
    }

    merged[existingIndex] = {
      ...merged[existingIndex],
      ...incomingMessage,
      ...(incomingMessage?._id ? { isSending: false } : {}),
    };
    getMessageKeys(merged[existingIndex]).forEach((key) =>
      indexByKey.set(key, existingIndex),
    );
  }

  return merged;
};

const preserveReadState = ({
  existingContact,
  incomingContact,
  authUserId,
  isSelectedChat,
  recentlyRead,
  isAdminView,
}) => {
  const shouldPreserve = Boolean(authUserId) && (isSelectedChat || recentlyRead);
  if (!shouldPreserve) {
    return {
      unreadStaffIds: incomingContact.unreadStaffIds,
      unreadCounts: incomingContact.unreadCounts || existingContact.unreadCounts,
    };
  }

  // Admin/owner acknowledged → team-wide clear (matches optimistic UI intent).
  // Peer unread will re-raise on next server push if it's genuinely unread for them.
  if (isAdminView) {
    const serverCounts =
      incomingContact.unreadCounts || existingContact.unreadCounts || {};
    return {
      unreadStaffIds: [],
      unreadCounts: Object.fromEntries(
        Object.keys(serverCounts).map((k) => [k, 0]),
      ),
    };
  }

  return {
    unreadStaffIds: (existingContact.unreadStaffIds || []).filter(
      (staffId) => staffId !== authUserId,
    ),
    unreadCounts: {
      ...(incomingContact.unreadCounts || existingContact.unreadCounts || {}),
      [authUserId]: 0,
    },
  };
};

export const mergeRealtimeContact = (
  existingContact,
  incomingContact,
  {
    authUserId,
    isSelectedChat = false,
    recentlyRead = false,
    isAdminView = false,
    preserveExistingMessages = true,
    mergeMessages = false,
  } = {},
) => {
  const { unreadStaffIds, unreadCounts } = preserveReadState({
    existingContact,
    incomingContact,
    authUserId,
    isSelectedChat,
    recentlyRead,
    isAdminView,
  });

  const mergedContact = {
    ...existingContact,
    ...incomingContact,
    unreadStaffIds,
    unreadCounts,
  };

  if (
    mergeMessages &&
    preserveExistingMessages &&
    Array.isArray(existingContact.messages) &&
    existingContact.messages.length > 0 &&
    Array.isArray(incomingContact.messages) &&
    incomingContact.messages.length > 0
  ) {
    mergedContact.messages = mergeRealtimeMessages(
      existingContact.messages,
      incomingContact.messages,
    );
  } else if (
    preserveExistingMessages &&
    Array.isArray(existingContact.messages) &&
    existingContact.messages.length > 0
  ) {
    mergedContact.messages = existingContact.messages;
  } else if (incomingContact.messages !== undefined) {
    mergedContact.messages = incomingContact.messages;
  } else if (existingContact.messages !== undefined) {
    mergedContact.messages = existingContact.messages;
  }

  return mergedContact;
};

const matchesRealtimeContact = (existingContact, incomingContact) => {
  const incomingShopId = incomingContact?._shopId || incomingContact?.shopId;
  const existingShopId = existingContact?._shopId || existingContact?.shopId;

  if (incomingContact?._id && existingContact?._id) {
    return existingContact._id === incomingContact._id;
  }

  if (!incomingContact?.uid || existingContact?.uid !== incomingContact.uid) {
    return false;
  }

  if (!incomingShopId || !existingShopId) return true;
  return existingShopId === incomingShopId;
};

export const upsertRealtimeContact = (
  prevContacts,
  incomingContact,
  {
    selectedCategories = [],
    authUserId,
    isSelectedChat = false,
    recentlyRead = false,
    isAdminView = false,
    insertAtStart = true,
    preserveExistingMessages = true,
    mergeMessages = false,
  } = {},
) => {
  const existingIndex = prevContacts.findIndex((contact) =>
    matchesRealtimeContact(contact, incomingContact),
  );

  if (!shouldIncludeRealtimeContact(incomingContact, selectedCategories)) {
    if (existingIndex === -1) return prevContacts;
    return prevContacts.filter((_, index) => index !== existingIndex);
  }

  if (existingIndex === -1) {
    return insertAtStart
      ? [incomingContact, ...prevContacts]
      : [...prevContacts, incomingContact];
  }

  const nextContacts = [...prevContacts];
  nextContacts[existingIndex] = mergeRealtimeContact(
    prevContacts[existingIndex],
    incomingContact,
    {
      authUserId,
      isSelectedChat,
      recentlyRead,
      isAdminView,
      preserveExistingMessages,
      mergeMessages,
    },
  );
  return nextContacts;
};

export const mergeRealtimeContactPage = (
  prevContacts,
  incomingContacts,
  {
    selectedCategories = [],
    authUserId,
    isSelectedChat = () => false,
    recentlyRead = () => false,
    isAdminView = false,
    preserveExistingMessages = true,
    mergeMessages = false,
  } = {},
) => {
  const remainingContacts = [...prevContacts];
  const nextContacts = [];

  for (const incomingContact of incomingContacts) {
    if (!incomingContact) continue;

    const existingIndex = remainingContacts.findIndex((contact) =>
      matchesRealtimeContact(contact, incomingContact),
    );
    const existingContact =
      existingIndex >= 0 ? remainingContacts.splice(existingIndex, 1)[0] : null;

    if (!shouldIncludeRealtimeContact(incomingContact, selectedCategories)) {
      continue;
    }

    if (!existingContact) {
      nextContacts.push(incomingContact);
      continue;
    }

    nextContacts.push(
      mergeRealtimeContact(existingContact, incomingContact, {
        authUserId,
        isSelectedChat: isSelectedChat(incomingContact, existingContact),
        recentlyRead: recentlyRead(incomingContact, existingContact),
        isAdminView,
        preserveExistingMessages,
        mergeMessages,
      }),
    );
  }

  return [...nextContacts, ...remainingContacts];
};

export const runDedupedHydration = (inflightMap, key, loader) => {
  if (inflightMap.has(key)) return inflightMap.get(key);

  const promise = Promise.resolve()
    .then(loader)
    .finally(() => {
      inflightMap.delete(key);
    });

  inflightMap.set(key, promise);
  return promise;
};
